import AnimatedScore from '@/components/AnimatedScore/AnimatedScore'
import Team from '@/components/Team/Team'
import Trapezoid from '@/components/Trapezoid/Trapezoid'
import { getSportConfig, SPORT_ID } from '@/config/sports'
import { useTheme } from '@/contexts/ThemeContext'
import { useTemplateAnimation } from '@/hooks/useTemplateAnimation'
import { getHomeCompetitor, getAwayCompetitor } from '@/services/jsonDataService'
import { hexToRgba } from '@/utils/colorUtils'
import { POSITION_CLASSES, POSITION_ANIMATIONS } from '@/utils/scorebugPosition'

import CardDivs from './CardDivs'
import { resolveCardColors, totalCardCount } from './cards'
import { deriveElapsedSeconds, formatClock, resolveBreakLabel, resolvePeriodLabel, useTickingNow } from './clock'
import './GoalSportBug.css'

import type { JsonMatchData, SoccerScore, GoalSportEventState, ScoreBugPosition } from '@/services/jsonDataService'
import type { MatchData } from '@/types'
import type { FC } from 'react'

export interface GoalSportBugProps {
  matchData?: MatchData
  jsonData: JsonMatchData | null
  isVisible?: boolean
  scorebugPosition?: ScoreBugPosition
}

const MULTIPLIERS = {
  teamSize: 0,
  scoreBoxSize: 2,
  scoreFontSize: 1.1,
  cardWidth: 0.18,
  cardHeight: 0.5,
  periodFontSize: 0.45,
  clockFontSize: 0.55,
  statePanelHeight: 1.05,
  statePanelSize: 4.4,
  // Trapezoid (variant="left") clips its left edge with a polygon that hides
  // ~baseHeight*0.5 of pixels from the left. Push content right by that much
  // so labels never sit under the clipped slant.
  statePanelLeftPad: 0.55,
}

const GoalSportBug: FC<GoalSportBugProps> = ({ jsonData, isVisible = true, scorebugPosition = 'bottom-left' }) => {
  const theme = useTheme()
  const { in: animationType } = POSITION_ANIMATIONS[scorebugPosition]
  const animationRef = useTemplateAnimation(isVisible, {
    animationType,
    duration: 0.5,
  })

  // Sport config drives labels (1H/2H, Half Time, Full Time, etc.).
  // Defaults to Soccer since it's the reference instance.
  const sportId = (jsonData?.sportId as SPORT_ID | undefined) ?? SPORT_ID.SOCCER
  const sportConfig = getSportConfig(sportId)
  const periodLabels = sportConfig?.periodLabels
  const breakLabels = sportConfig?.breakLabels
  const endLabel = sportConfig?.endLabel ?? 'Full Time'

  // Card palette is sport-driven. Soccer: yellow/red. Field hockey adds
  // green. Netball: empty (cards row hidden).
  const cardColors = resolveCardColors(sportId)

  // Get home and away competitors
  const homeCompetitor = getHomeCompetitor(jsonData)
  const awayCompetitor = getAwayCompetitor(jsonData)

  const homeScore = homeCompetitor?.score as SoccerScore | undefined
  const awayScore = awayCompetitor?.score as SoccerScore | undefined

  const homeGoals = homeScore?.goals ?? 0
  const awayGoals = awayScore?.goals ?? 0
  const homeCards = homeScore?.cards ?? {}
  const awayCards = awayScore?.cards ?? {}

  const eventState = jsonData?.eventState as GoalSportEventState | undefined
  const phase = eventState?.phase ?? 'PRE'
  const period = Math.max(1, eventState?.period ?? 1)

  // Tick re-renders only while RUNNING; BREAK/END/PRE values are static so
  // there's nothing to redraw.
  const isRunning = phase === 'RUNNING'
  const now = useTickingNow(isRunning)
  const elapsedSeconds = deriveElapsedSeconds(eventState, now)
  const clockText = formatClock(elapsedSeconds)

  // Period label resolves from sport config; phase determines which one to show.
  let periodLabel: string
  if (phase === 'PRE') {
    periodLabel = resolvePeriodLabel(1, periodLabels)
  } else if (phase === 'BREAK') {
    periodLabel = resolveBreakLabel(period, breakLabels)
  } else if (phase === 'END') {
    periodLabel = endLabel
  } else {
    periodLabel = resolvePeriodLabel(period, periodLabels)
  }

  // Clock visibility: hide on PRE (no kickoff yet); show on RUNNING/BREAK/END.
  const showClock = phase !== 'PRE'

  const positionClass = POSITION_CLASSES[scorebugPosition]
  const statePanelColumnsSize = MULTIPLIERS.statePanelSize

  const homeHasCards = totalCardCount(homeCards, cardColors) > 0
  const awayHasCards = totalCardCount(awayCards, cardColors) > 0

  return (
    <div className={`goal-sport-bug-content-wrapper ${positionClass}`}>
      <div ref={animationRef} className="goal-sport-bug-panel" data-scorebug-panel>
        {/* Header: period + clock badge sits on the right above the team rows */}
        <div className="goal-sport-bug-header">
          <Trapezoid
            variant="left"
            style={{
              background: hexToRgba(theme.colors.primary, 0.95),
              width: `calc(${theme.baseHeight} * ${statePanelColumnsSize})`,
              height: `calc(${theme.baseHeight} * ${MULTIPLIERS.statePanelHeight})`,
              justifyContent: 'center',
              gap: `calc(${theme.baseHeight} * 0.4)`,
              paddingLeft: `calc(${theme.baseHeight} * ${MULTIPLIERS.statePanelLeftPad})`,
              paddingRight: `calc(${theme.baseHeight} * 0.25)`,
              boxSizing: 'border-box',
            }}
          >
            <span
              className="goal-sport-bug-period-label"
              style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.periodFontSize})` }}
            >
              {periodLabel}
            </span>
            {showClock && (
              <span
                className="goal-sport-bug-clock"
                style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.clockFontSize})` }}
              >
                {clockText}
              </span>
            )}
          </Trapezoid>
        </div>

        {/* Team rows */}
        <div
          className="goal-sport-bug-teams"
          style={{
            background: hexToRgba(theme.colors.primary, 0.95),
          }}
        >
          {/* Home team row */}
          <div className="goal-sport-bug-team-row">
            <Team data={homeCompetitor} isHome style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
            {homeHasCards && (
              <CardDivs
                cards={homeCards}
                cardColors={cardColors}
                baseHeight={theme.baseHeight}
                widthMultiplier={MULTIPLIERS.cardWidth}
                heightMultiplier={MULTIPLIERS.cardHeight}
                className="goal-sport-bug-cards-column"
                ariaPrefix="Home "
              />
            )}
            <div className="goal-sport-bug-scores-column">
              <div
                className="goal-sport-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
              >
                <AnimatedScore
                  value={homeGoals}
                  className="goal-sport-bug-score-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                />
              </div>
            </div>
          </div>

          {/* Away team row */}
          <div className="goal-sport-bug-team-row">
            <Team data={awayCompetitor} style={{ flex: 1, minWidth: `${MULTIPLIERS.teamSize}rem` }} />
            {awayHasCards && (
              <CardDivs
                cards={awayCards}
                cardColors={cardColors}
                baseHeight={theme.baseHeight}
                widthMultiplier={MULTIPLIERS.cardWidth}
                heightMultiplier={MULTIPLIERS.cardHeight}
                className="goal-sport-bug-cards-column"
                ariaPrefix="Away "
              />
            )}
            <div className="goal-sport-bug-scores-column">
              <div
                className="goal-sport-bug-score-box"
                style={{ width: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreBoxSize})` }}
              >
                <AnimatedScore
                  value={awayGoals}
                  className="goal-sport-bug-score-value"
                  style={{ fontSize: `calc(${theme.baseHeight} * ${MULTIPLIERS.scoreFontSize})` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="goal-sport-bug-footer"
          style={{
            background: hexToRgba(theme.colors.secondary, 0.95),
            height: `calc(${theme.baseHeight} * 0.8)`,
          }}
        >
          <img
            src="/assets/frogbox-logo-mono.png"
            alt="FrogBox"
            className="goal-sport-bug-branding-logo"
            style={{ height: `calc(${theme.baseHeight} * 0.4)` }}
          />
        </div>
      </div>
    </div>
  )
}

export default GoalSportBug
